import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, TrendingDown, BarChart3, ArrowUp, ArrowDown,
  History, Loader2, AlertTriangle, Lock, Save, X, Edit3,
  FileText, DollarSign, Activity, ChevronRight,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api, { formatErrorMessage } from '@/utils/api';

const AREAS = ['Contabilidad', 'Tributaria', 'Legal', 'Comercial', 'Adm. y Finanzas'];
const AREAS_OP = ['Contabilidad', 'Tributaria', 'Legal'];
const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const BLOQUES = [
  { id: 'facturacion', label: 'Facturación', icon: FileText, color: 'indigo' },
  { id: 'recaudacion', label: 'Recaudación', icon: DollarSign, color: 'emerald' },
  { id: 'gastos', label: 'Gastos c/Exigencia', icon: Activity, color: 'rose' },
];

const fmt = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  return '$' + Math.round(Number(n)).toLocaleString('es-CL');
};
const fmtPct = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${Number(n).toFixed(1)}%`;
};

// flag → estilo Tailwind
const flagClass = (flag) => {
  switch (flag) {
    case 'up_pos':    return 'text-emerald-700 bg-emerald-50';
    case 'down_neg':  return 'text-rose-700 bg-rose-50';
    case 'down_pos':  return 'text-emerald-700 bg-emerald-50';
    case 'up_neg':    return 'text-rose-700 bg-rose-50';
    default:          return 'text-slate-500 bg-slate-50';
  }
};
const flagArrow = (flag) => {
  if (flag === 'up_pos' || flag === 'up_neg') return <ArrowUp className="w-3 h-3 inline" />;
  if (flag === 'down_pos' || flag === 'down_neg') return <ArrowDown className="w-3 h-3 inline" />;
  return null;
};

const currentYear = new Date().getFullYear();
const YEARS = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

export default function ReporteBITab() {
  const now = new Date();
  const [anio, setAnio] = useState(now.getFullYear());
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [area, setArea] = useState('Consolidado');
  const [bloqueActivo, setBloqueActivo] = useState('facturacion');
  const [auditOpen, setAuditOpen] = useState(false);

  return (
    <div className="space-y-4">
      <Card className="p-4 border-slate-200">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Reporte Comparativo BI</h2>
              <p className="text-xs text-slate-500">
                Análisis MoM, YoY y YTD con semáforos automáticos por bloque.
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setAuditOpen(true)} data-testid="bi-audit-btn">
            <History className="w-3.5 h-3.5 mr-1" /> Auditoría
          </Button>
        </div>

        {/* Filtros globales */}
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex flex-col gap-1">
            <Label className="text-[11px] text-slate-500">Año</Label>
            <select
              value={anio} onChange={e => setAnio(Number(e.target.value))}
              className="h-9 px-3 border border-slate-300 rounded bg-white text-sm w-28"
              data-testid="bi-anio"
            >
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-[11px] text-slate-500">Mes referencia</Label>
            <select
              value={mes} onChange={e => setMes(Number(e.target.value))}
              className="h-9 px-3 border border-slate-300 rounded bg-white text-sm w-36"
              data-testid="bi-mes"
            >
              {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-[11px] text-slate-500">Área de negocio</Label>
            <select
              value={area} onChange={e => setArea(e.target.value)}
              className="h-9 px-3 border border-slate-300 rounded bg-white text-sm"
              data-testid="bi-area"
            >
              <option value="Consolidado">Ver Todas (Consolidado)</option>
              {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </div>
      </Card>

      {/* KPI Eficiencia de Cobranza */}
      <KPICard anio={anio} mes={mes} />

      {/* Bloques A, B, C */}
      <Tabs value={bloqueActivo} onValueChange={setBloqueActivo} className="w-full">
        <TabsList className="grid grid-cols-3 max-w-2xl">
          {BLOQUES.map(b => (
            <TabsTrigger key={b.id} value={b.id} data-testid={`bi-tab-${b.id}`} className="text-xs">
              <b.icon className="w-3.5 h-3.5 mr-1" />
              {b.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {BLOQUES.map(b => (
          <TabsContent key={b.id} value={b.id} className="mt-3">
            <BloqueTable bloque={b} anio={anio} mes={mes} area={area} />
          </TabsContent>
        ))}
      </Tabs>

      {auditOpen && <AuditDialog onClose={() => setAuditOpen(false)} />}
    </div>
  );
}

// ============================================================
// KPI Eficiencia Cobranza
// ============================================================
const KPICard = ({ anio, mes }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/bi/eficiencia-cobranza?anio=${anio}&mes=${mes}`);
        setData(data);
      } catch (e) {
        toast.error(formatErrorMessage(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [anio, mes]);

  if (loading || !data) return null;
  const c = data.consolidado;
  const alertaConsol = c.alerta;

  return (
    <Card className="p-0 border-slate-200 overflow-hidden">
      <div className="px-4 py-2.5 bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <Activity className="w-4 h-4 text-blue-600" />
            Eficiencia de Cobranza · {data.mes_label} {anio}
          </h3>
          <p className="text-[11px] text-slate-500">Ratio = Recaudación ÷ Facturación · alerta si {'<'} 80%</p>
        </div>
        <div className={`px-4 py-2 rounded-lg font-bold text-xl ${alertaConsol ? 'bg-amber-100 text-amber-800' : (c.ratio_pct === null ? 'bg-slate-100 text-slate-500' : 'bg-emerald-100 text-emerald-800')}`}>
          {c.ratio_pct === null ? '—' : `${c.ratio_pct.toFixed(1)}%`}
          <span className="block text-[10px] font-normal opacity-80 text-center">Consolidado</span>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border-t border-slate-200">
        {data.areas.map(a => (
          <div key={a.area} className="p-3 border-r border-slate-100 last:border-r-0" data-testid={`bi-ratio-${a.area}`}>
            <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{a.area}</p>
            <p className={`text-lg font-bold tabular-nums ${a.alerta ? 'text-amber-700' : (a.ratio_pct === null ? 'text-slate-400' : 'text-slate-800')}`}>
              {a.ratio_pct === null ? '—' : `${a.ratio_pct.toFixed(1)}%`}
              {a.alerta && <AlertTriangle className="w-3.5 h-3.5 inline ml-1 text-amber-600" />}
            </p>
            <p className="text-[10px] text-slate-500 mt-0.5">
              Fact: {fmt(a.facturacion)}<br />Rec: {fmt(a.recaudacion)}
            </p>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ============================================================
// Tabla Bloque (Facturación / Recaudación / Gastos)
// ============================================================
const BloqueTable = ({ bloque, anio, mes, area }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // { area }
  const [editVal, setEditVal] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const areaParam = area && area !== 'Consolidado' ? `&area=${encodeURIComponent(area)}` : '';
      const { data } = await api.get(`/bi/comparativo?bloque=${bloque.id}&anio=${anio}&mes=${mes}${areaParam}`);
      setData(data);
    } catch (e) {
      toast.error(formatErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [bloque.id, anio, mes, area]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (a, cur) => {
    setEditing({ area: a });
    setEditVal(String(Math.round(cur || 0)));
  };
  const cancelEdit = () => { setEditing(null); setEditVal(''); };
  const saveEdit = async () => {
    if (!editing) return;
    const num = Number(editVal.replace(/\D/g, ''));
    try {
      await api.post('/bi/override', {
        bloque: bloque.id, anio, mes, area: editing.area, valor: num,
        motivo: `Override ${bloque.label}`,
      });
      toast.success('Override aplicado');
      cancelEdit();
      load();
    } catch (e) {
      toast.error(formatErrorMessage(e));
    }
  };

  if (loading && !data) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }
  if (!data) return null;

  return (
    <Card className="p-0 border-slate-200 overflow-hidden" data-testid={`bi-table-${bloque.id}`}>
      <div className={`px-4 py-2.5 bg-${bloque.color}-50 border-b border-${bloque.color}-200`}>
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <bloque.icon className={`w-4 h-4 text-${bloque.color}-600`} />
          Matriz de {bloque.label} · {data.mes_label} {anio}
          {bloque.id === 'gastos' && (
            <span className="text-[10px] text-slate-500 font-normal">Lectura invertida (↓ es positivo)</span>
          )}
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Área</th>
              <th className="text-right px-3 py-2 font-semibold">V. Mes Actual</th>
              <th className="text-right px-3 py-2 font-semibold">ΔMoM $</th>
              <th className="text-right px-3 py-2 font-semibold">ΔMoM %</th>
              <th className="text-right px-3 py-2 font-semibold">ΔYoY $</th>
              <th className="text-right px-3 py-2 font-semibold">ΔYoY %</th>
              <th className="text-right px-3 py-2 font-semibold">Promedio YTD</th>
              {data.puede_editar && <th className="px-2 py-2"></th>}
            </tr>
          </thead>
          <tbody>
            {data.filas.map(f => (
              <tr key={f.area} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`bi-row-${bloque.id}-${f.area}`}>
                <td className="px-3 py-2 font-semibold text-slate-800">{f.area}</td>
                <td className="text-right px-3 py-2 tabular-nums">
                  {editing?.area === f.area ? (
                    <span className="inline-flex items-center gap-1">
                      <Input
                        value={editVal}
                        onChange={(e) => setEditVal(e.target.value)}
                        className="h-6 w-28 text-xs text-right inline-block"
                        autoFocus
                        onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
                      />
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={saveEdit}><Save className="w-3 h-3" /></Button>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={cancelEdit}><X className="w-3 h-3" /></Button>
                    </span>
                  ) : (
                    <span>{fmt(f.v_actual)}</span>
                  )}
                </td>
                <td className={`text-right px-3 py-2 tabular-nums ${flagClass(f.mom_nominal_flag)}`}>
                  {flagArrow(f.mom_nominal_flag)} {fmt(f.mom_nominal)}
                </td>
                <td className={`text-right px-3 py-2 tabular-nums ${flagClass(f.mom_pct_flag)}`}>
                  {fmtPct(f.mom_pct)}
                </td>
                <td className={`text-right px-3 py-2 tabular-nums ${flagClass(f.yoy_nominal_flag)}`}>
                  {flagArrow(f.yoy_nominal_flag)} {fmt(f.yoy_nominal)}
                </td>
                <td className={`text-right px-3 py-2 tabular-nums ${flagClass(f.yoy_pct_flag)}`}>
                  {fmtPct(f.yoy_pct)}
                </td>
                <td className="text-right px-3 py-2 tabular-nums text-slate-700">{fmt(f.ytd_average)}</td>
                {data.puede_editar && (
                  <td className="px-2 py-2 text-right">
                    {editing?.area !== f.area && (
                      <button
                        onClick={() => startEdit(f.area, f.v_actual)}
                        className="text-slate-400 hover:text-indigo-600 p-1 rounded"
                        title="Override manual"
                        data-testid={`bi-edit-${bloque.id}-${f.area}`}
                      >
                        <Edit3 className="w-3 h-3" />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {/* Consolidado */}
            {data.consolidado && (
              <tr className="bg-indigo-50 border-t-2 border-indigo-200 font-bold">
                <td className="px-3 py-2 text-slate-900">Consolidado</td>
                <td className="text-right px-3 py-2 tabular-nums text-indigo-900">{fmt(data.consolidado.v_actual)}</td>
                <td className={`text-right px-3 py-2 tabular-nums ${flagClass(data.consolidado.mom_nominal_flag)}`}>
                  {flagArrow(data.consolidado.mom_nominal_flag)} {fmt(data.consolidado.mom_nominal)}
                </td>
                <td className={`text-right px-3 py-2 tabular-nums ${flagClass(data.consolidado.mom_nominal_flag)}`}>
                  {fmtPct(data.consolidado.mom_pct)}
                </td>
                <td className={`text-right px-3 py-2 tabular-nums ${flagClass(data.consolidado.yoy_nominal_flag)}`}>
                  {flagArrow(data.consolidado.yoy_nominal_flag)} {fmt(data.consolidado.yoy_nominal)}
                </td>
                <td className={`text-right px-3 py-2 tabular-nums ${flagClass(data.consolidado.yoy_nominal_flag)}`}>
                  {fmtPct(data.consolidado.yoy_pct)}
                </td>
                <td className="text-right px-3 py-2 tabular-nums">{fmt(data.consolidado.ytd_average)}</td>
                {data.puede_editar && <td></td>}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!data.puede_editar && (
        <div className="px-3 py-1.5 bg-amber-50 border-t border-amber-200 text-[11px] text-amber-800 flex items-center gap-1">
          <Lock className="w-3 h-3" />
          Solo lectura. cmoreno, jcarrasco y dliempe pueden aplicar overrides.
        </div>
      )}
    </Card>
  );
};

// ============================================================
// Auditoría
// ============================================================
const AuditDialog = ({ onClose }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/bi/auditoria');
        setRows(data.items || []);
      } finally { setLoading(false); }
    })();
  }, []);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="bi-audit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-slate-600" /> Auditoría BI · Overrides
          </DialogTitle>
        </DialogHeader>
        {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> :
          rows.length === 0 ? <p className="text-sm text-slate-500 italic text-center py-4">Sin ediciones registradas.</p> :
            <table className="w-full text-xs">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left px-2 py-1.5">Fecha</th>
                  <th className="text-left px-2 py-1.5">Usuario</th>
                  <th className="text-left px-2 py-1.5">Bloque</th>
                  <th className="text-left px-2 py-1.5">Período</th>
                  <th className="text-left px-2 py-1.5">Área</th>
                  <th className="text-right px-2 py-1.5">Antes</th>
                  <th className="text-right px-2 py-1.5">Después</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={idx} className="border-t border-slate-100">
                    <td className="px-2 py-1.5">{new Date(r.timestamp).toLocaleString('es-CL')}</td>
                    <td className="px-2 py-1.5">{r.user_email}</td>
                    <td className="px-2 py-1.5"><span className="px-1.5 py-0.5 bg-slate-100 rounded">{r.bloque}</span></td>
                    <td className="px-2 py-1.5">{r.periodo}</td>
                    <td className="px-2 py-1.5">{r.area}</td>
                    <td className="text-right px-2 py-1.5 tabular-nums">{fmt(r.old_value)}</td>
                    <td className="text-right px-2 py-1.5 tabular-nums">{fmt(r.new_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
        }
        <DialogFooter><Button variant="outline" onClick={onClose}>Cerrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
